/**
 * Masking a player's personal data (ADR-0003).
 *
 * A **masked field** is shown with most of its characters hidden — phone,
 * email, LINE id, bank account number. A **hidden field** is a different state
 * entirely: not returned at all (birth date, registration IP). The two words
 * are kept apart here, in `apps/member/CONTEXT.md`, and in the UI copy.
 *
 * Masking happens inside the owning service, before the value leaves it. The
 * full value reaches a staff member only through the per-field reveal endpoint,
 * which records who looked at what. A browser that receives a masked string can
 * leak nothing, which is the whole point — a client-side mask is a decoration.
 *
 * The one rule every helper here obeys: **never mask a value into nothing.** A
 * short account number or a two-character LINE id that came back as `••••` is
 * indistinguishable from a missing one, and a staff member reading "no account
 * on file" when there is one will act on it. Short inputs therefore keep fewer
 * characters rather than losing all of them.
 *
 * Null, undefined and empty strings pass straight through: absence is not a
 * secret, and turning `null` into a string of bullets would invent a value.
 */

/** The character every mask is built from. Not an ellipsis, not an asterisk. */
const DOT = '•';

/**
 * Does this string look like something one of the helpers above produced?
 *
 * A masked value is a READ artefact and must never travel back in as a WRITE.
 * A console that pre-fills an edit form from a masked read and submits every
 * field replaces the player's real phone number, email, LINE id or payout
 * account number with a row of dots — silently, because the audit trail then
 * records masked→masked.
 *
 * Derived from the same `DOT` the masks are built from, so a change to the mask
 * character cannot leave the two halves disagreeing. The test is deliberately
 * "contains the character" rather than a shape match: every helper above emits
 * at least one dot, no real phone number, email address, LINE id or account
 * number contains U+2022, and a partial paste of a masked value is just as
 * wrong as the whole one.
 */
export function looksMasked(value: unknown): boolean {
  return typeof value === 'string' && value.includes(DOT);
}

function dots(count: number): string {
  return DOT.repeat(Math.max(count, 1));
}

/** Nothing to mask — the value is absent, not hidden. */
function isBlank(value: string | null | undefined): value is null | undefined | '' {
  return value === null || value === undefined || value === '';
}

/**
 * Phone: keep the first three and the last four digits.
 *
 *   `0812345678` → `081•••5678`
 *
 * Under eight characters there is no middle left to hide without eating one of
 * the ends, so only the last two survive (`1234` → `••34`). The mask always
 * carries at least one dot, so a masked value never reads as a full one.
 */
export function maskPhone(value: string | null | undefined): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  if (v.length < 8) return dots(Math.max(v.length - 2, 1)) + v.slice(-2);
  return v.slice(0, 3) + dots(v.length - 7) + v.slice(-4);
}

/**
 * Email: keep the first character of the local part and the whole domain.
 *
 *   `somchai@gmail.com` → `s•••@gmail.com`
 *
 * The domain stays because it is what a staff member matches against what the
 * customer is reading out; the local part is the identifying half. A value with
 * no `@` is treated as a LINE-id-shaped string rather than guessed at.
 */
export function maskEmail(value: string | null | undefined): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  const at = v.indexOf('@');
  if (at <= 0) return maskLineId(v);
  return v[0] + dots(3) + v.slice(at);
}

/**
 * LINE id: keep the first two characters.
 *
 *   `somchai99` → `so•••`
 *
 * A one-character id keeps that one character rather than becoming pure dots.
 */
export function maskLineId(value: string | null | undefined): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  return v.slice(0, Math.min(2, v.length)) + dots(3);
}

/**
 * Bank account number: keep the last four digits.
 *
 *   `1234567890` → `••••7890`
 *
 * Under six characters the last four would be nearly the whole number, so only
 * the last two survive. Four dots regardless of the real length: the length of
 * an account number is itself a hint about which bank issued it.
 */
export function maskBankAccount(value: string | null | undefined): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  if (v.length < 6) return dots(2) + v.slice(-2);
  return dots(4) + v.slice(-4);
}

/** How much of a device fingerprint survives a mask. */
const FINGERPRINT_PREFIX = 8;

/**
 * IP address: keep the network half, hide the host half.
 *
 *   `203.0.113.45` → `203.0.•••.•••`
 *   `2001:db8:85a3::8a2e:370:7334` → `2001:db8:•••`
 *
 * A masked IP is still a GROUPING key to the eye — two players behind one home
 * router still read as the same neighbourhood — while the value that identifies
 * one household to a subpoena, a geo-lookup or a rival operator does not leave.
 * That is the whole reason it is a mask and not a hidden field: the surfaces
 * that carry an IP exist BECAUSE accounts share one, so blanking it would
 * delete the finding along with the identifier.
 *
 * IPv6 collapses to one run of dots rather than one per group: how many groups
 * an address was written with, and where its `::` fell, is itself a hint, and
 * an embedded IPv4 (`::ffff:203.0.113.45`) would otherwise survive whole in the
 * last "group".
 *
 * Anything with neither separator is not an address this function recognises,
 * so it keeps two characters the way a LINE id does — never nothing.
 */
export function maskIp(value: string | null | undefined): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  if (v.includes(':')) return `${v.split(':').slice(0, 2).join(':')}:${dots(3)}`;
  if (v.includes('.')) {
    const octets = v.split('.');
    const hidden = octets.slice(2);
    const kept = octets.slice(0, 2).join('.');
    if (hidden.length === 0) return `${kept}.${dots(3)}`;
    return [kept, ...hidden.map(() => dots(3))].join('.');
  }
  return v.slice(0, Math.min(2, v.length)) + dots(3);
}

/**
 * Device fingerprint: keep a short leading prefix.
 *
 *   `9f4c1a7be2d05386fa` → `9f4c1a7b•••`
 *
 * Same bargain as the IP one rung up. The prefix is STABLE — the same device
 * always masks to the same string — so an analyst can still see that three
 * accounts came from one machine, which is what the Online tab and the
 * multi-account clusters are for. What they cannot do is copy the value out and
 * carry it somewhere it identifies a person.
 *
 * A fingerprint at or under the prefix length keeps two characters instead, so
 * a short one is not returned whole with decoration.
 */
export function maskDeviceFingerprint(
  value: string | null | undefined,
): string | null | undefined {
  if (isBlank(value)) return value;
  const v = value.trim();
  if (v === '') return value;
  const keep = v.length > FINGERPRINT_PREFIX ? FINGERPRINT_PREFIX : Math.min(2, v.length);
  return v.slice(0, keep) + dots(3);
}

/** The two columns that say WHERE a request came from and on WHAT. */
export interface LocationFields {
  ipAddress?: unknown;
  deviceFingerprint?: unknown;
}

/**
 * Where and on what, masked together, because they are read together.
 *
 * svc-monitor's presence rows and svc-audit's append-only trail carry the same
 * two columns under the same two names, and each had its own copy of this loop.
 * They agreed, which is the only reason the duplication was invisible; the next
 * time ADR-0003 moves — a third identifying column, a different prefix length —
 * one copy moves and the other does not.
 *
 * Unconditional: the caller has already made the DECISION (`piiDecisionFor`),
 * and a masker that re-asks is a second place for the answer to differ. Only
 * keys the row actually carries are touched, so a projection that left one out
 * does not gain a `null` it never had.
 */
export function maskLocationFields<T extends LocationFields>(row: T): T {
  const out = { ...row } as Record<string, unknown>;
  if ('ipAddress' in row) out['ipAddress'] = maskIp(row.ipAddress as string | null | undefined);
  if ('deviceFingerprint' in row) {
    out['deviceFingerprint'] = maskDeviceFingerprint(
      row.deviceFingerprint as string | null | undefined,
    );
  }
  return out as T;
}

/**
 * Which field a reveal asks for. The endpoint refuses anything else.
 *
 * Both STATES are here, not just the masked one. `birthDate` and `registeredIp`
 * are HIDDEN — withheld from every list and drawer, at every rung — but hidden
 * is a statement about the routine read, not a statement that nobody may ever
 * see the value. The permission decides that, and the reveal endpoint is where
 * it is decided and recorded (ADR-0003). Leaving them off this list meant the
 * only caller that held `member:reveal_pii` could not reveal the two fields the
 * console had blanked for it, which is the permission saying one thing and the
 * list saying another.
 *
 * `fieldsForLevel` is untouched by this: the LEVEL projection still decides what
 * a routine read returns, and a rung below MANAGER still never sees these two on
 * the drawer. Only the per-field door, which writes an audit row every time it
 * opens, admits them.
 */
export const REVEALABLE_FIELDS = [
  'phone',
  'email',
  'lineId',
  'bankAccount',
  'birthDate',
  'registeredIp',
] as const;

export type RevealableField = (typeof REVEALABLE_FIELDS)[number];

export function isRevealableField(value: string): value is RevealableField {
  return (REVEALABLE_FIELDS as readonly string[]).includes(value);
}

/**
 * The gateway's answer to "does this caller hold `member:reveal_pii`".
 *
 * The `x-perm-` prefix is on the proxy's untrusted-inbound list, so a client
 * cannot forge the stamp and an ABSENT header means "not granted", never "not
 * asked" — a service must read absence as a denial.
 */
export const REVEAL_PII_HEADER = 'x-perm-reveal-pii';

export function hasRevealPermission(request: { headers: Headers }): boolean {
  return request.headers.get(REVEAL_PII_HEADER) === '1';
}

/**
 * Who a mask applies to, as ONE predicate every service that serialises member
 * PII reads.
 *
 * `admin` and `partner` are both STAFF: they are the two kinds `auditRead`
 * records a read for, and a set that named only one of them let a partner token
 * read a player's contact details and bank numbers in full, leaving no trail
 * either. A PLAYER is not staff — they own the data — and neither is a trusted
 * internal caller, which carries no JWT at all.
 *
 * The exception that is not a player: an IMPERSONATION token. "Login as member"
 * mints a `kind: 'player'` token carrying `impersonatedBy`, and the subject
 * behind it is a staff member. A kind test alone therefore handed any account
 * holding `member:impersonate` the whole record with no per-field record of what
 * was read, so a token carrying `impersonatedBy` is staff wherever this decision
 * is made — and the reveal stamp must not lift it: the per-field reveal endpoint
 * on the backoffice audience is the one door that records who looked.
 *
 * Two services serialise the same player's bank account number — svc-member on
 * the drawer, svc-finance on the money queues — and a set that lived in one of
 * them was the way around the other: same data, one page over.
 */
const STAFF_KINDS = new Set(['admin', 'partner']);

/** The two claims a token makes that this decision reads. */
export interface PiiCaller {
  kind?: string | undefined;
  /** The admin id that minted an impersonation token. */
  impersonatedBy?: string | undefined;
}

/** A staff member wearing a player's session, whatever `kind` says. */
export function isImpersonatedSession(auth: PiiCaller | null | undefined): boolean {
  return auth?.impersonatedBy !== undefined && auth.impersonatedBy !== '';
}

/** Whether a mask applies to this caller at all, before any reveal stamp. */
export function isStaffPiiCaller(auth: PiiCaller | null | undefined): boolean {
  return isImpersonatedSession(auth) || (auth?.kind !== undefined && STAFF_KINDS.has(auth.kind));
}
